<script setup lang="ts">
import IconeSvg from '@/components/ui/IconeSvg.vue'
import { useAvisosStore } from '@/stores/avisos'

const avisos = useAvisosStore()
</script>

<template>
  <!--
    `aria-live="polite"` na região, e não em cada recado: assim o leitor de tela
    anuncia o que entra sem interromper o que a pessoa está fazendo. `role="status"`
    dispensa o foco — roubar o foco para dizer "salvo" é pior que não dizer nada.
  -->
  <div class="avisos" role="status" aria-live="polite">
    <TransitionGroup name="aviso">
      <div
        v-for="aviso in avisos.lista"
        :key="aviso.id"
        class="aviso"
        :class="`aviso--${aviso.tom}`"
      >
        <IconeSvg :nome="aviso.tom === 'ok' ? 'confere' : 'alerta'" :tamanho="16" />
        <p class="aviso__texto">{{ aviso.texto }}</p>
        <button
          type="button"
          class="aviso__fechar"
          aria-label="Dispensar"
          @click="avisos.dispensar(aviso.id)"
        >
          <IconeSvg nome="fechar" :tamanho="13" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.avisos {
  position: fixed;
  right: var(--e-4);
  bottom: var(--e-4);
  z-index: var(--z-aviso);
  display: grid;
  gap: var(--e-2);
  justify-items: end;
  /* A região não intercepta cliques; só os cartões dentro dela. */
  pointer-events: none;
}

.aviso {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  max-width: min(28rem, calc(100vw - var(--e-6)));
  padding: var(--e-3) var(--e-3) var(--e-3) var(--e-4);
  border: 1px solid var(--borda);
  border-radius: var(--raio-lg);
  background: var(--superficie);
  box-shadow: var(--sombra-3);
  pointer-events: auto;
}

.aviso--ok {
  border-color: color-mix(in srgb, var(--sucesso) 40%, transparent);
  color: var(--sucesso);
}

.aviso--erro {
  border-color: color-mix(in srgb, var(--erro) 40%, transparent);
  color: var(--erro);
}

.aviso__texto {
  margin: 0;
  flex: 1;
  font-size: var(--t-sm);
  color: var(--texto);
}

.aviso__fechar {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  flex: none;
  border: 0;
  border-radius: var(--raio);
  background: none;
  color: var(--texto-fraco);
  cursor: pointer;
}

.aviso__fechar:hover {
  background: var(--superficie-2);
  color: var(--texto);
}

/* Entra deslizando da direita, de onde ele aparece; sai só desbotando, porque
   saída animada demais rouba atenção de quem já leu. */
.aviso-enter-active {
  transition: all 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.aviso-leave-active {
  transition: all 180ms ease-in;
  position: absolute;
}

.aviso-enter-from {
  opacity: 0;
  translate: 1rem 0;
}

.aviso-leave-to {
  opacity: 0;
}

.aviso-move {
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
